import type { ProgramReview } from '../../interfaces/CamProgramming.interface';
import { resolveCompletedSessionStartedAt } from './sessionLogging';

export type ReviewStatus = 'pending' | 'in_progress' | 'done';

export type ReviewLogStatus = 'in_progress' | 'completed';

export function reviewStatus(
	record: Pick<ProgramReview, 'review_started_at' | 'review_completed_at'>
): ReviewStatus {
	if (record.review_completed_at) {
		return 'done';
	}
	if (record.review_started_at) {
		return 'in_progress';
	}
	return 'pending';
}

/** Derive the log/edit modal status from stored timestamps. */
export function reviewLogStatus(
	startedAt: string | null | undefined,
	completedAt: string | null | undefined
): ReviewLogStatus {
	if (completedAt) {
		return 'completed';
	}
	if (startedAt) {
		return 'in_progress';
	}
	return 'in_progress';
}

export const REVIEW_LOG_STATUS_LABELS: Record<ReviewLogStatus, string> = {
	in_progress: 'In progress',
	completed: 'Completed',
};

export interface ReviewStatsPayloadOptions {
	isEdit?: boolean;
	existingStartedAt?: string | null;
	existingCompletedAt?: string | null;
	now?: () => string;
}

export interface ReviewStatsValues {
	review_status: ReviewLogStatus;
	review_duration_minutes: number | null;
	reviewer?: string | null;
	feedback?: string;
}

export const buildReviewStatsPayload = (
	values: ReviewStatsValues,
	allowReviewerOverride: boolean,
	options: ReviewStatsPayloadOptions = {}
) => {
	const now = options.now ?? (() => new Date().toISOString());
	const wasCompleted =
		!!options.existingCompletedAt &&
		reviewLogStatus(options.existingStartedAt, options.existingCompletedAt) ===
			'completed';

	const shared = {
		feedback: values.feedback || '',
		...(allowReviewerOverride ? { reviewer: values.reviewer || null } : {}),
	};

	if (values.review_status === 'in_progress') {
		const startedAt =
			options.isEdit && options.existingStartedAt
				? options.existingStartedAt
				: now();
		return {
			...shared,
			review_started_at: startedAt,
			review_completed_at: null,
			review_duration_minutes: 0,
		};
	}

	const completedAt =
		options.isEdit && wasCompleted && options.existingCompletedAt
			? options.existingCompletedAt
			: now();
	const duration = values.review_duration_minutes ?? 0;
	const startedAt = resolveCompletedSessionStartedAt(
		options.existingStartedAt,
		completedAt,
		duration
	);

	return {
		...shared,
		review_duration_minutes: duration,
		review_completed_at: completedAt,
		...(startedAt ? { review_started_at: startedAt } : {}),
	};
};
